import React, {
  useMemo,
  useRef,
  useEffect,
  Fragment,
  useState,
  useCallback
} from 'react'
import {
  ColumnDef,
  Row,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable
} from '@tanstack/react-table'
import { TextInput } from '@siftscience/focus-components/input'
import { Button, IconButton } from '@siftscience/focus-components/button'
import { Delete } from '@siftscience/focus-components/icons/Delete'
import { Title } from '@siftscience/focus-components/text'
import { v4 as uuid } from 'uuid'
import { makeStyles } from '@material-ui/core/styles'
import { capitalize, get, snakeCase, cloneDeep, isEqual } from 'lodash'
import { getDatasetPath } from '../../utils/det'
import { DataField, DatasetDTO } from './dtos'
import SubTable from './sub-table'

export type RowData = Record<string, any>

const useStyles = makeStyles(theme => ({
  nestedArrayField: {
    minWidth: 0
  },
  mainTableActions: {
    marginTop: '16px',
    marginBottom: '16px'
  },
  subComponentContainer: {
    padding: '16px',
    backgroundColor: '#f9f9f9',
    border: `1px solid ${theme.palette.divider}`,
    borderTop: 'none'
  },
  requiredAsterisk: {
    color: '#eb3b2e'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'auto',
    '& th, & td': {
      border: `1px solid ${theme.palette.divider}`,
      padding: '8px',
      textAlign: 'left',
      whiteSpace: 'nowrap'
    },
    '& th': {
      backgroundColor: '#f2f2f2'
    }
  },
  tableContainer: {
    display: 'block',
    overflowX: 'auto',
    '-webkit-overflow-scrolling': 'touch'
  }
}))
// --- MAIN COMPONENT ---

interface NestedArrayFieldProps {
  dataset: DatasetDTO
  dataField: DataField
  changes: Record<string, any>
  onArrayChange: (newArray: RowData[], field: DataField) => void
  onInputBlur: (field: DataField) => void
}

const NestedArrayField = ({
  dataset,
  dataField,
  changes,
  onArrayChange,
  onInputBlur
}: NestedArrayFieldProps): React.ReactElement => {
  const classes = useStyles()
  const [internalData, setInternalData] = useState<RowData[]>([])
  const internalDataRef = useRef(internalData)

  useEffect(() => {
    const externalData =
      changes[dataField?.datasetPath] ??
      get(dataset, getDatasetPath(dataField?.datasetPath))

    if (!isEqual(internalDataRef.current, externalData)) {
      const dataToChange = cloneDeep(externalData || [])
      setInternalData(dataToChange)
      internalDataRef.current = dataToChange
    }
  }, [internalDataRef, dataset, dataField, changes])

  const handleBlur = useCallback(() => {
    onArrayChange(internalDataRef.current, dataField)
    onInputBlur(dataField)
  }, [dataField, onArrayChange, onInputBlur])

  const onMainTableChange = useCallback(
    (value: string, rowIndex: number, accessorKey: string) => {
      const prevData = internalDataRef.current
      const newData = prevData.map((row, index) => {
        if (index === rowIndex) {
          return { ...row, [accessorKey]: value }
        }
        return row
      })
      internalDataRef.current = newData
      setInternalData(newData)
    },
    [internalDataRef]
  )

  const onSubTableChange = useCallback(
    (newSubArray: RowData[], rowIndex: number, accessorKey: string) => {
      const prevData = internalDataRef.current
      const newData = prevData.map((row, index) => {
        if (index === rowIndex) {
          return { ...row, [accessorKey]: newSubArray }
        }
        return row
      })
      internalDataRef.current = newData
      setInternalData(newData)
    },
    [internalDataRef]
  )

  const mainProperties = useMemo(() => {
    const schema = dataField?.jsonSchema
    return (
      schema?.items?.[0]?.properties?.properties ||
      schema?.items?.[0]?.properties ||
      schema?.items?.properties ||
      {}
    )
  }, [dataField.jsonSchema])

  const onAddMainRow = () => {
    const newRow = Object.keys(mainProperties).reduce((acc, key) => {
      const prop = mainProperties[key]
      acc[snakeCase(key)] = prop.type === 'array' ? [] : ''
      return acc
    }, {})
    const newData = [...internalDataRef.current, newRow]
    internalDataRef.current = newData
    setInternalData(newData)
    onArrayChange(newData, dataField)
  }

  const onDeleteMainRow = (rowIndex: number) => {
    const newArray = [...internalDataRef.current]
    newArray.splice(rowIndex, 1)
    internalDataRef.current = newArray
    setInternalData(newArray)
    onArrayChange(newArray, dataField)
  }

  const columns: ColumnDef<RowData>[] = useMemo(() => {
    const properties = mainProperties
    const schema = dataField?.jsonSchema
    const requiredFields =
      schema?.items?.[0]?.properties?.required ||
      schema?.items?.[0]?.required ||
      schema?.items?.required ||
      []
    const requiredFieldsHash = requiredFields.reduce((hash, field) => {
      hash[field] = true
      return hash
    }, {})

    const mainColumns: ColumnDef<RowData>[] = [
      {
        id: 'expander',
        header: () => null,
        cell: ({ row }) =>
          row.getCanExpand() ? (
            <button
              {...{
                onClick: row.getToggleExpandedHandler(),
                style: {
                  cursor: 'pointer',
                  border: 'none',
                  background: 'transparent'
                }
              }}
            >
              {row.getIsExpanded() ? '👇' : '👉'}
            </button>
          ) : null,
        size: 40
      },
      ...Object.keys(properties)
        .filter(
          key => !(properties[key].type === 'array' && properties[key].items)
        )
        .map(key => {
          const snakeCaseKey = snakeCase(key)
          const isRequired =
            requiredFieldsHash[properties[key]?.key || key] || false
          return {
            accessorKey: snakeCaseKey,
            header: () => (
              <>
                {capitalize(properties[key]?.title || key)}
                {isRequired && (
                  <span className={classes.requiredAsterisk}>*</span>
                )}
              </>
            ),
            cell: ({ row, getValue }) => {
              const value = getValue<string>()
              return (
                <TextInput
                  value={value}
                  onChange={e =>
                    onMainTableChange(e.target.value, row.index, snakeCaseKey)
                  }
                  onBlur={handleBlur}
                  containerStyle={{ width: '100%' }}
                  required={isRequired}
                  containerClassName={`${isRequired && !value ? 'error' : ''}`}
                />
              )
            }
          }
        }),
      {
        id: 'delete',
        cell: ({ row }) => (
          <IconButton
            variant="secondary-ghost"
            onClick={() => onDeleteMainRow(row.index)}
          >
            <Delete />
          </IconButton>
        ),
        size: 60
      }
    ]
    return mainColumns
  }, [dataField, mainProperties, onMainTableChange, handleBlur])

  const renderSubComponent = useCallback(
    ({ row }: { row: Row<RowData> }) => {
      const properties = mainProperties
      const arrayProperties = Object.keys(properties).filter(
        key => properties[key].type === 'array' && properties[key].items
      )

      return (
        <td colSpan={columns.length} className={classes.subComponentContainer}>
          {arrayProperties.map(key => {
            const snakeCaseKey = snakeCase(key)
            return (
              <div key={key} className={classes.subTableWrapper}>
                <SubTable
                  data={row.original[snakeCaseKey] || []}
                  schema={properties[key]}
                  title={properties[key].title || key}
                  onSubChange={newSubArray =>
                    onSubTableChange(newSubArray, row.index, snakeCaseKey)
                  }
                  onBlur={handleBlur}
                />
              </div>
            )
          })}
        </td>
      )
    },
    [mainProperties, columns, onSubTableChange, handleBlur]
  )

  const table = useReactTable({
    data: internalData, // Use internal state for the table
    columns,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel()
  })

  return (
    <div className={classes.nestedArrayField}>
      <Title>
        {dataField.name}
        {dataField?.required && (
          <span className={classes.requiredAsterisk}>*</span>
        )}
        :
      </Title>
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <Fragment key={row.id}>
                <tr>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
                {row.getIsExpanded() && <tr>{renderSubComponent({ row })}</tr>}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className={classes.mainTableActions}>
        <Button variant="secondary" lined onClick={onAddMainRow}>
          + Add {capitalize(dataField.name.slice(0, -1))}
        </Button>
      </div>
    </div>
  )
}

export default NestedArrayField
